import React, { useRef, useEffect, useState } from "react";
import { FaBold, FaItalic, FaUnderline, FaListUl, FaListOl } from "react-icons/fa";
import "./styles.scss";

const TextEditor = ({ placeholder = "Type here...", className, onChange, value, name }) => {
  const editorRef = useRef(null);
  const [content, setContent] = useState(value || "");
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    insertUnorderedList: false,
    insertOrderedList: false
  });
  const [isEmpty, setIsEmpty] = useState(!value);

  // Helper function to check if content is empty (including <br> tags)
  const isContentEmpty = (html) => {
    if (html === null || html === undefined) return true;

    let htmlStr;
    if (typeof html === "string") {
      htmlStr = html;
    } else if (typeof html === "number" || typeof html === "boolean") {
      htmlStr = String(html);
    } else {
      return true;
    }

    // Replace <br> tags and whitespace with empty string
    const cleanHtml = htmlStr.replace(/<br\s*\/?>/gi, "").trim();
    return cleanHtml === "";
  };

  // Initialize the editor once
  useEffect(() => {
    if (!isInitialized && editorRef.current) {
      if (value && !isContentEmpty(value)) {
        editorRef.current.innerHTML = value;
        setIsEmpty(false);
      } else {
        editorRef.current.innerHTML = "";
        setIsEmpty(true);
      }
      setIsInitialized(true);
    }
  }, [isInitialized, value, placeholder]);

  // Update editor content when value prop changes
  useEffect(() => {
    if (editorRef.current && value !== undefined && isInitialized) {
      // Only update if the value is different from current content
      // This prevents cursor jumping and text reversing
      const currentContent = editorRef.current.innerHTML;

      if (value !== currentContent && document.activeElement !== editorRef.current) {
        // Check if the value is just a <br> tag or empty
        if (isContentEmpty(value)) {
          editorRef.current.innerHTML = "";
          setIsEmpty(true);
        } else {
          editorRef.current.innerHTML = value;
          setIsEmpty(false);
        }
      }
    }
  }, [value, isInitialized]);

  // Handle focus and blur for placeholder
  useEffect(() => {
    const handleFocus = () => {
      checkActiveFormatsForThisEditor();
    };

    const handleBlur = () => {
      const editor = editorRef.current;
      if (editor && isContentEmpty(editor.innerHTML)) {
        setIsEmpty(true);

        // When blurring an empty editor, ensure we send an empty string to parent
        if (onChange && (editor.innerHTML !== "")) {
          onChange({
            target: {
              name,
              value: ""
            }
          });
        }
      }
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener("focus", handleFocus);
      editor.addEventListener("blur", handleBlur);
    }

    return () => {
      if (editor) {
        editor.removeEventListener("focus", handleFocus);
        editor.removeEventListener("blur", handleBlur);
      }
    };
  }, [onChange, name, placeholder]);

  // Check for active formats specific to this editor instance
  const checkActiveFormatsForThisEditor = () => {
    // Only check if this editor has focus
    if (editorRef.current && document.activeElement === editorRef.current) {
      setActiveFormats({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList')
      });
    }
  };

  // Listen for selection and format changes on this specific editor
  useEffect(() => {
    const handleSelectionChange = () => {
      checkActiveFormatsForThisEditor();
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('mouseup', handleSelectionChange);
      editor.addEventListener('keyup', handleSelectionChange);
      editor.addEventListener('input', checkActiveFormatsForThisEditor);
    }

    // We're attaching selectionchange to the document, but only act if our editor is focused
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      if (editor) {
        editor.removeEventListener('mouseup', handleSelectionChange);
        editor.removeEventListener('keyup', handleSelectionChange);
        editor.removeEventListener('input', checkActiveFormatsForThisEditor);
      }
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  // Function to apply formatting
  const handleFormat = (command) => {
    if (editorRef.current) {
      // Focus the editor first to make sure we're formatting the correct instance
      editorRef.current.focus();

      // If the editor is empty and we're trying to format, add a space first
      if (isContentEmpty(editorRef.current.innerHTML)) {
        editorRef.current.innerHTML = " ";
        // Create a range at the end of the editor content
        const range = document.createRange();
        const sel = window.getSelection();
        range.setStart(editorRef.current.childNodes[0], 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }

      // Execute the command
      document.execCommand(command, false, null);

      // Update active formats
      checkActiveFormatsForThisEditor();

      // Update content
      const newContent = editorRef.current.innerHTML;
      const newIsEmpty = isContentEmpty(newContent);

      setContent(newIsEmpty ? "" : newContent);
      setIsEmpty(newIsEmpty);

      if (onChange) {
        onChange({
          target: {
            name,
            value: newIsEmpty ? "" : newContent
          }
        });
      }
    }
  };

  const handlePaste = (e) => {
    if (name === "imgObservation") return;
    e.preventDefault();

    const html = e.clipboardData.getData("text/html");
    if (html) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Allowed tags for the editor
      // const allowedTags = ["b", "strong", "i", "em", "u", "ul", "ol", "li", "label"];
      const allowedTags = ["b", "strong", "i", "em", "u", "ul", "ol", "li", "label", "div", "p", "br"];

      // Remove unwanted styles and tags
      const cleanHTML = sanitizeHTML(tempDiv, allowedTags);

      document.execCommand("insertHTML", false, cleanHTML);
    } else {
      // If no HTML, insert plain text
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    }
  };

  const sanitizeHTML = (element, allowedTags) => {
    // Process the DOM tree in a way that's safe during modification
    const nodesToRemove = [];

    // First pass: identify nodes to remove
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_ELEMENT, null, false);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const tagName = node.tagName.toLowerCase();

      // Clean attributes regardless of whether we keep the tag
      for (let i = node.attributes.length - 1; i >= 0; i--) {
        const attrName = node.attributes[i].name;
        if (attrName !== "href" && attrName !== "src") {
          node.removeAttribute(attrName);
        }
      }

      if (!allowedTags.includes(tagName)) {
        nodesToRemove.push(node);
      }
    }

    // Second pass: remove unwanted nodes and preserve their children
    nodesToRemove.forEach(node => {
      const parent = node.parentNode;
      if (parent) {
        while (node.firstChild) {
          parent.insertBefore(node.firstChild, node);
        }
        parent.removeChild(node);
      }
    });

    // Handle comment nodes separately
    const removeComments = (node) => {
      const childNodes = Array.from(node.childNodes);
      childNodes.forEach(child => {
        if (child.nodeType === 8) { // Comment node
          node.removeChild(child);
        } else if (child.nodeType === 1) { // Element node
          removeComments(child);
        }
      });
    };

    removeComments(element);

    return element.innerHTML;
  };


  // Handle input changes
  const handleInput = (e) => {
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      const newIsEmpty = isContentEmpty(newContent);

      setContent(newIsEmpty ? "" : newContent);
      setIsEmpty(newIsEmpty);

      if (onChange) {
        onChange({
          target: {
            name,
            value: newIsEmpty ? "" : newContent
          }
        });
      }

      // Update formatting buttons
      checkActiveFormatsForThisEditor();
    }
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && name === "imgObservation") {
      const MAX_CHAR = 100;

      const handleKeyDown = (e) => {
        const textContent = editor.innerText?.trim() || "";
        const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
        if (textContent.length >= MAX_CHAR && !allowedKeys.includes(e.key)) {
          e.preventDefault();
        }
      };

      const handlePaste = (e) => {
        e.preventDefault();

        const clipboardText = e.clipboardData.getData("text/plain");
        const currentText = editor.innerText?.trim() || "";
        const remaining = MAX_CHAR - currentText.length;
        console.log(remaining, "remain")
        if (remaining === 0) return; // Do not paste anything

        const textToPaste = clipboardText.slice(0, remaining);
        document.execCommand("insertText", false, textToPaste);
      };

      editor.addEventListener("keydown", handleKeyDown);
      editor.addEventListener("paste", handlePaste);

      return () => {
        editor.removeEventListener("keydown", handleKeyDown);
        editor.removeEventListener("paste", handlePaste);
      };
    }
  }, [name]);



  return (
    <div className="editor-container">
      {/* Toolbar for formatting */}
      <div className="toolbar">
        <button
          type="button"
          onClick={() => handleFormat("bold")}
          aria-label="Bold"
          className={activeFormats.bold ? 'active' : ''}
        >
          <FaBold />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("italic")}
          aria-label="Italic"
          className={activeFormats.italic ? 'active' : ''}
        >
          <FaItalic />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("underline")}
          aria-label="Underline"
          className={activeFormats.underline ? 'active' : ''}
        >
          <FaUnderline />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("insertUnorderedList")}
          aria-label="Bullet List"
          className={activeFormats.insertUnorderedList ? 'active' : ''}
        >
          <FaListUl />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("insertOrderedList")}
          aria-label="Numbered List"
          className={activeFormats.insertOrderedList ? 'active' : ''}
        >
          <FaListOl />
        </button>
      </div>

      {/* Editable Content Div */}
      <div className="editor-wrapper">
        {isEmpty && (
          <div className="editor-placeholder">{placeholder}</div>
        )}
        <div
          ref={editorRef}
          onPaste={handlePaste}
          contentEditable={true}
          className={`editor ${className || ""}`}
          onInput={handleInput}
          suppressContentEditableWarning={true}
        />
      </div>
    </div>
  );
};

export default TextEditor;